'use client'

// xoá sau khi chuyển bảng sang nam <=> nữ
import { TableSize } from '@/components/common/table-size'
import { Divider } from '@/components/ui/divider'
import { useUserQuery } from '@/hooks/react-query/user/use-user'
import { PRODUCT_SIZE } from '@/lib/constants/size'
import { LocalStorage } from '@/lib/utils/local-storage'
import { __cProductService } from '@/services/client/__c.product.service'
import { ProductDetailFormatted } from '@/types/product-detail'
import {
  SizeCalculationMaleParams,
  SizeDataCategory,
  SizeMaleSuggestion,
} from '@/types/size'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'
import { useProductDetailSafe } from '../../context/product-detail-context'
import { GuideNote } from '../product-info-sizes/guide-note'
import { SizeGuideMaleContent } from './size-guide-content'
import { SizeNote } from './size-note'

// Tạo key cho cache
const getSizeCacheKey = (params: SizeCalculationMaleParams): string[] => [
  'size-suggestion',
  `${params.height}`,
  `${params.weight}`,
  params.product_id ?? '',
  params.type,
]

interface ProductInfoSizeMaleProps {
  productId?: string
  product: ProductDetailFormatted
  sizeChartOverride?: string
  sizeGuideOverride?: SizeDataCategory
}

const EXCLUDED_SIZE_CHARTS = [
  '691be073e23ee3205b5e2dd0',
  '691fd178b11a865d5b0b683b',
]

export default function ProductInfoSizeMale({
  productId,
  product,
  sizeChartOverride,
  sizeGuideOverride,
}: ProductInfoSizeMaleProps) {
  const queryClient = useQueryClient()
  const t = useTranslations('product_detail.title')
  const { setSizeSelected } = useProductDetailSafe()

  const { data: user } = useUserQuery()
  const profileHeight =
    user?.height && user.height >= 155 && user.height <= 190
      ? user.height
      : undefined
  const profileWeight =
    user?.weight && user.weight >= 40 && user.weight <= 90
      ? user.weight
      : undefined

  // Initialize state with values from localStorage if available
  const initialData =
    LocalStorage.get<SizeCalculationMaleParams>('MALE_SIZE_DATA')

  const [height, setHeight] = useState<number>(
    profileHeight ?? initialData?.height ?? 155,
  )
  const [weight, setWeight] = useState<number>(
    profileWeight ?? initialData?.weight ?? 40,
  )
  const [recommendedSize, setRecommendedSize] =
    useState<SizeMaleSuggestion | null>(null)

  const [debouncedHeight] = useDebounceValue(height, 300)
  const [debouncedWeight] = useDebounceValue(weight, 300)

  const effectiveSizeChart =
    sizeChartOverride ?? PRODUCT_SIZE[product.sizechart] ?? ''
  const effectiveSizeGuide = sizeGuideOverride ?? product?.size_guide
  const effectiveProductId = productId ?? product.id ?? ''
  const sizeType = effectiveSizeChart

  // Tạo params cho mutation
  const sizeParams = useMemo((): SizeCalculationMaleParams => {
    return {
      height: debouncedHeight,
      weight: debouncedWeight,
      type: sizeType || '',
      product_id: effectiveProductId,
    }
  }, [debouncedHeight, debouncedWeight, sizeType, effectiveProductId])

  // Tạo cache key
  const cacheKey = useMemo(() => {
    return getSizeCacheKey(sizeParams)
  }, [sizeParams])

  // Save to localStorage whenever values change
  useEffect(() => {
    LocalStorage.set('MALE_SIZE_DATA', {
      height,
      weight,
      type: sizeType || '',
      product_id: effectiveProductId,
    })
  }, [height, weight, sizeType, effectiveProductId])

  const { mutate: calculateSize, isPending } = useMutation({
    mutationFn: (params: SizeCalculationMaleParams) =>
      __cProductService.getSizeMaleSuggestion(params),
    onSuccess: (data) => {
      if (data) {
        setRecommendedSize(data)

        // Lưu kết quả vào cache
        queryClient.setQueryData(cacheKey, data)

        // Tự động active size vừa tính toán được
        if (setSizeSelected && data.size && data.size !== 'Không tìm thấy') {
          setSizeSelected(data.size)
        }
      }
    },
  })

  return (
    <>
      {!EXCLUDED_SIZE_CHARTS.includes(product.sizechart) ? (
        <SizeGuideMaleContent
          height={height}
          weight={weight}
          setHeight={setHeight}
          setWeight={setWeight}
          recommendedSize={recommendedSize}
          isLoading={isPending}
          sizeType={sizeType}
          productId={effectiveProductId}
          calculateSize={calculateSize}
          shapeType={product?.shape_type}
        />
      ) : null}
      {effectiveSizeGuide ? (
        <div className='mt-5 md:mt-10'>
          <div className='text-sm font-semibold uppercase'>
            {t('specifications')}
          </div>
          <Divider className='mb-3 mt-2' />
          <div className='mb-2 text-[13px] text-neutral-600'>
            {t('male_specifications_note')}
          </div>
          <TableSize
            rows={effectiveSizeGuide.rows}
            columns={effectiveSizeGuide.columns}
            matrix={effectiveSizeGuide.matrix}
            headerTitle={effectiveSizeGuide.headerTitle}
            leftTitle={effectiveSizeGuide.leftTitle}
            colClassName='min-w-32 text-start'
          />

          <SizeNote sizeType={sizeType} />

          <GuideNote />
        </div>
      ) : null}
    </>
  )
}
